/**
 * ENTRAID-350: the Microsoft Graph application permissions a customer's own Entra app can request, why each is needed,
 * and which permissions every data source needs. This file is data plus pure functions: it is the single definition of
 * "what a customer app declares" for a selection, used by the manifest builder, the consent checks and the UI.
 *
 * The groups are a UI grouping only (spec D4). The real contract is SOURCE_REQUIRED_PERMISSIONS: one token carries every
 * permission the customer consented, so a source is not tied to its group. The mapping is a hypothesis until the TEST
 * per-source probe (spec 15.3) passes; the map changes if the probe disagrees (spec Q5, Q6).
 */

export const GRAPH_RESOURCE_APP_ID = '00000003-0000-0000-c000-000000000000';

/** The permission every selection carries: the join key of every dataset, Watch recipients and the tenant proof. */
export const CORE_PERMISSION = 'User.Read.All';

export const PERMISSION_GROUPS = Object.freeze([
  { id: 'core', title: 'Core', permissions: ['User.Read.All', 'Group.Read.All', 'GroupMember.Read.All'] },
  { id: 'directory', title: 'Directory and apps', permissions: ['Directory.Read.All', 'Organization.Read.All'] },
  { id: 'audit', title: 'Audit and sign-ins', permissions: ['AuditLog.Read.All'] },
  {
    id: 'security',
    title: 'Security and risk',
    permissions: [
      'Policy.Read.All', 'SecurityEvents.Read.All', 'DirectoryRecommendations.Read.All', 'IdentityRiskEvent.Read.All',
      'IdentityRiskyUser.Read.All', 'UserAuthenticationMethod.Read.All', 'AccessReview.Read.All',
      'RoleManagementPolicy.Read.Directory', 'OnPremDirectorySynchronization.Read.All',
    ],
  },
  { id: 'm365', title: 'Microsoft 365 usage', permissions: ['Reports.Read.All', 'Sites.Read.All'] },
].map((g) => Object.freeze({ ...g, permissions: Object.freeze([...g.permissions]) })));

/** What onGuard reads with the permission, and what it never does (spec 4.1). Shown to the admin before consent. */
export const PERMISSION_JUSTIFICATIONS = Object.freeze({
  'User.Read.All': 'Reads user profiles and license names (users, per-user license details and assignment states, assigned plans). No mailbox, no files, no passwords. With Audit and sign-ins on the same token and Microsoft Entra ID P1 or P2 it also returns last sign-in dates.',
  'Group.Read.All': 'Reads group names, visibility and owners. No group content, no conversations.',
  'GroupMember.Read.All': 'Reads who belongs to which group.',
  'Directory.Read.All': 'Reads directory objects: devices and owners, admin roles, app registrations, service principals, OAuth consent grants, domains, eligible roles, group settings and license plans. Directory objects only. Broad by Microsoft design.',
  'Organization.Read.All': 'Reads the tenant profile, the organizational branding logo, the certificate authority count, and the Microsoft products and subscriptions the tenant bought (seat counts, trials, renewal dates). Never changes a license.',
  'AuditLog.Read.All': 'Reads directory audit events, sign-in logs (they contain IP addresses and locations), MFA registration details, log retention and last sign-in dates on users (needs Microsoft Entra ID P1 or P2).',
  'Policy.Read.All': 'Reads Conditional Access policies, named locations, the admin consent workflow policy and the authentication methods policy. Policy settings only.',
  'SecurityEvents.Read.All': 'Reads Secure Score and its control profiles: scores and improvement actions.',
  'DirectoryRecommendations.Read.All': 'Reads Microsoft tenant recommendations: advice text and status.',
  'IdentityRiskEvent.Read.All': 'Reads Identity Protection risk detections (needs Microsoft Entra ID P2). Read only, no remediation.',
  'IdentityRiskyUser.Read.All': 'Reads users currently flagged at risk (needs Microsoft Entra ID P2).',
  'UserAuthenticationMethod.Read.All': 'Reads which MFA methods each user registered and sign-in preferences. Method types only.',
  'AccessReview.Read.All': 'Reads which access reviews exist and their status, no reviewer identities (needs Microsoft Entra ID P2).',
  'RoleManagementPolicy.Read.Directory': 'Reads the activation rules of Global Administrator and Privileged Role Administrator (needs Microsoft Entra ID P2).',
  'OnPremDirectorySynchronization.Read.All': 'Reads whether the tenant syncs from on-premises Active Directory and whether password hash sync is on.',
  'Reports.Read.All': 'Reads daily usage reports: Teams, SharePoint, OneDrive, Outlook, Groups. Per-user counts, no content.',
  'Sites.Read.All': 'Reads the SharePoint site list: names, URLs, dates. No files, no pages.',
});

/** All 17 permissions, sorted. */
export const ALL_PERMISSIONS = Object.freeze(PERMISSION_GROUPS.flatMap((g) => g.permissions).sort());

const U = 'User.Read.All';
const D = 'Directory.Read.All';
const O = 'Organization.Read.All';
const A = 'AuditLog.Read.All';
const P = 'Policy.Read.All';

/** Every permission a source needs (not only the extra ones). The manifest of a custom selection is the union of these. */
export const SOURCE_REQUIRED_PERMISSIONS = Object.freeze(Object.fromEntries(Object.entries({
  // Core
  'users': [U],
  'groups': ['Group.Read.All'],
  'group-memberships': ['Group.Read.All', 'GroupMember.Read.All'],
  'user-licenses': [U],
  'assigned-plans': [U],
  // Directory and apps
  'user-roles': [D],
  // Access Reviews (spec 2026-10-26, 11.1): roleAssignments and roleDefinitions under Directory.Read.All, already granted.
  'role-assignments': [D],
  'organization': [O],
  'organization-branding': [O],
  'domains': [D],
  'devices': [D],
  'device-owners': [D],
  'app-registrations': [D],
  'service-principals': [D],
  'oauth2-grants': [D],
  'certificate-authorities': [O],
  // License Insights (spec 7.1): no new permission, Essentials already grants it.
  'subscribed-skus': [O],
  'subscriptions': [O],
  'password-protection': [D],
  'eligible-roles': [D],
  // Audit and sign-ins (sign-ins is documented as also needing Directory.Read.All; verify in the probe)
  'audit-logs': [A],
  'sign-ins': [A, D],
  'registration-details': [A],
  'audit-log-retention': [A],
  // Security and risk
  'conditional-access': [P],
  'named-locations': [P],
  'secure-score': ['SecurityEvents.Read.All'],
  'secure-score-profiles': ['SecurityEvents.Read.All'],
  'risk-detections': ['IdentityRiskEvent.Read.All'],
  'risky-users': ['IdentityRiskyUser.Read.All'],
  'recommendations': ['DirectoryRecommendations.Read.All'],
  'auth-methods': ['UserAuthenticationMethod.Read.All', U],
  'sign-in-preferences': ['UserAuthenticationMethod.Read.All', U],
  'admin-consent-policy': [P],
  'authentication-methods-policy': [P],
  'access-reviews': ['AccessReview.Read.All'],
  'role-management-policies': ['RoleManagementPolicy.Read.Directory'],
  'on-premises-sync': ['OnPremDirectorySynchronization.Read.All'],
  // Microsoft 365 usage
  'sites': ['Sites.Read.All'],
  'reports': ['Reports.Read.All'],
}).map(([id, list]) => [id, Object.freeze([...list].sort())])));

export const SELECTION_MODES = Object.freeze(['all', 'custom']);

/**
 * Validates a selection from a request body. Returns { ok: true, selection: { mode, sources } } (sources sorted, unique;
 * empty for `all`) or { ok: false, error }. An unknown source id is refused, never ignored.
 */
export function normalizeSelection(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return { ok: false, error: 'selection must be an object' };
  const { mode } = input;
  if (!SELECTION_MODES.includes(mode)) return { ok: false, error: 'mode must be "all" or "custom"' };
  if (mode === 'all') return { ok: true, selection: { mode: 'all', sources: [] } };
  if (!Array.isArray(input.sources) || input.sources.length > 200) return { ok: false, error: 'sources must be a list' };
  const unknown = input.sources.filter((s) => typeof s !== 'string' || !Object.hasOwn(SOURCE_REQUIRED_PERMISSIONS, s));
  if (unknown.length > 0) return { ok: false, error: 'unknown data source' };
  return { ok: true, selection: { mode: 'custom', sources: [...new Set(input.sources)].sort() } };
}

/** The sorted permission list a (valid) selection declares on the customer app. `all` is the 17; `custom` is the minimal union plus Core. */
export function permissionsForSelection(selection) {
  const checked = normalizeSelection(selection);
  if (!checked.ok) throw new Error(`invalid permission selection: ${checked.error}`);
  if (checked.selection.mode === 'all') return [...ALL_PERMISSIONS];
  const set = new Set([CORE_PERMISSION]);
  for (const id of checked.selection.sources) for (const p of SOURCE_REQUIRED_PERMISSIONS[id]) set.add(p);
  return [...set].sort();
}

/** What changes between two permission lists: { added, removed } (both sorted). */
export function diffPermissions(before, after) {
  const b = new Set(before ?? []);
  const a = new Set(after ?? []);
  return { added: [...a].filter((p) => !b.has(p)).sort(), removed: [...b].filter((p) => !a.has(p)).sort() };
}

/** True when the permission set grants a source everything it needs. */
export function sourceSatisfied(sourceId, granted) {
  const needs = SOURCE_REQUIRED_PERMISSIONS[sourceId];
  if (!needs) return false;
  const have = new Set(granted ?? []);
  return needs.every((p) => have.has(p));
}
